<template>
  <div class="dashboard dashboard-lab">
    <b-container fluid class="dashboard-lab-container">
      <!-- Barra de herramientas -->
      <section class="outside-toolbar">
        <button class="btn btn-primary btn-sm" @click="toggleCreator">
          <i class="material-icons">add</i> Crear etiqueta
        </button>
        <span class="tag-counter">Etiquetas: {{ tags.length }}/50</span>
      </section>

      <!-- Panel de creación -->
      <section v-if="showCreator" class="creator-panel">
        <div class="creator-card">
          <h5>Nueva etiqueta</h5>
          <div class="creator-grid">
            <div>
              <label class="form-label">Título</label>
              <input
                v-model="draft.title"
                class="form-control form-control-sm"
                placeholder="Ej: Distribución de activos"
              />
            </div>
            <div>
              <label class="form-label">Tipo de gráfico</label>
              <select v-model="draft.presentationType" class="form-select form-select-sm">
                <option value="pie">Pastel</option>
                <option value="donut">Dona</option>
                <option value="bars">Barras</option>
                <option value="bars_horizontal">Barras horizontales</option>
                <option value="line">Línea</option>
                <option value="area">Área</option>
                <option value="gauge">Medidor</option>
                <option value="progress">Progreso</option>
                <option value="ranking">Ranking</option>
                <option value="value">Valor numérico</option>
              </select>
            </div>
            <div>
              <label class="form-label">Color de fondo</label>
              <input v-model="draft.color" type="color" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="creator-actions">
            <button class="btn btn-info btn-sm" @click="createTag">Guardar</button>
            <button class="btn btn-outline-secondary btn-sm" @click="showCreator = false">Cancelar</button>
          </div>
        </div>
      </section>

      <!-- Malla de etiquetas -->
      <section ref="canvas" class="tags-canvas">
        <div v-if="!tags.length" class="canvas-empty">
          <p>No hay etiquetas. Crea la primera para empezar.</p>
        </div>

        <article
          v-for="tag in tags"
          :key="tag.id"
          class="mini-tag"
          :class="{
            active: activeTagId === tag.id,
            editing: editingTagId === tag.id
          }"
          :style="tagStyle(tag)"
          @pointerdown="startDrag($event, tag.id)"
        >
          <header class="mini-tag-header">
            <strong>{{ tag.title }}</strong>
            <div class="mini-tag-actions">
              <button class="mini-edit" @click.stop="openTagEditor(tag.id)">✎</button>
              <button class="mini-delete" @click.stop="deleteTag(tag.id)">×</button>
            </div>
          </header>

          <div class="mini-tag-body">
            <!-- Editor -->
            <div v-if="editingTagId === tag.id" class="inline-editor" @pointerdown.stop>
              <div class="inline-grid">
                <div class="inline-field">
                  <small>Título</small>
                  <input v-model="editDraft.title" class="form-control form-control-sm" />
                </div>
                <div class="inline-field">
                  <small>Tipo</small>
                  <select v-model="editDraft.presentationType" class="form-select form-select-sm">
                    <option value="pie">Pastel</option>
                    <option value="donut">Dona</option>
                    <option value="bars">Barras</option>
                    <option value="bars_horizontal">Barras horizontales</option>
                    <option value="line">Línea</option>
                    <option value="area">Área</option>
                    <option value="gauge">Medidor</option>
                    <option value="progress">Progreso</option>
                    <option value="ranking">Ranking</option>
                    <option value="value">Valor numérico</option>
                  </select>
                </div>
              </div>
              <div class="inline-color-row">
                <input v-model="editDraft.color" type="color" />
                <button class="btn btn-info btn-sm" @click="saveTagEdit">Guardar</button>
                <button class="btn btn-outline-secondary btn-sm" @click="cancelTagEdit">Cancelar</button>
              </div>
            </div>

            <!-- Gráficos de ejemplo con datos inventados -->
            <template v-else>
              <!-- Gráfico de Pastel -->
              <div v-if="tag.presentationType === 'pie'" class="chart-panel">
                <div class="mini-pie" :style="{ background: pieGradient }"></div>
                <div class="mini-series-legend">
                  <span v-for="(item, idx) in pieData" :key="idx" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }} {{ item.percent }}%
                  </span>
                </div>
                <div class="metric-meta">
                  <span>Total: {{ pieData.reduce((sum, d) => sum + d.value, 0) }}</span>
                </div>
              </div>

              <!-- Gráfico de Dona -->
              <div v-if="tag.presentationType === 'donut'" class="chart-panel">
                <div class="mini-donut" :style="{ background: pieGradient }">
                  <span>{{ pieData.reduce((sum, d) => sum + d.value, 0) }}</span>
                </div>
                <div class="mini-series-legend">
                  <span v-for="(item, idx) in pieData" :key="idx" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }}
                  </span>
                </div>
              </div>

              <!-- Gráfico de Barras -->
              <div v-if="tag.presentationType === 'bars'" class="chart-panel bars-panel">
                <div class="mini-bars">
                  <div v-for="(item, idx) in barData" :key="idx" class="mini-bar-group">
                    <div class="mini-bar-item">
                      <div class="mini-bar-top-metrics">
                        <small class="mini-bar-value">{{ item.label }}</small>
                      </div>
                      <span class="mini-bar-fill" :style="{ height: `${item.height}%`, background: item.color }"></span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Gráfico de Barras Horizontales -->
              <div v-if="tag.presentationType === 'bars_horizontal'" class="chart-panel">
                <div v-for="(item, idx) in barData" :key="idx" class="hbar-row">
                  <span class="hbar-label">{{ item.label }}</span>
                  <div class="hbar-track">
                    <span class="hbar-fill" :style="{ width: `${item.height}%`, background: item.color }"></span>
                  </div>
                  <span class="hbar-value">{{ item.value }}</span>
                </div>
              </div>

              <!-- Gráfico de Línea -->
              <div v-if="tag.presentationType === 'line'" class="chart-panel">
                <div class="mini-line">
                  <svg class="line-svg" viewBox="0 0 100 100">
                    <polyline :points="linePoints" stroke="#4A90D9" stroke-width="2" fill="none" />
                    <circle v-for="(point, idx) in linePointsData" :key="idx" :cx="point.x" :cy="point.y" r="3" fill="#4A90D9" />
                  </svg>
                </div>
              </div>

              <!-- Gráfico de Área -->
              <div v-if="tag.presentationType === 'area'" class="chart-panel">
                <div class="mini-area">
                  <svg class="line-svg" viewBox="0 0 100 100">
                    <polygon :points="areaPoints" fill="rgba(74, 144, 217, 0.3)" />
                    <polyline :points="linePoints" stroke="#4A90D9" stroke-width="2" fill="none" />
                  </svg>
                </div>
              </div>

              <!-- Medidor -->
              <div v-if="tag.presentationType === 'gauge'" class="chart-panel">
                <div class="mini-gauge" :style="{ background: gaugeGradient }">
                  <span>{{ progressValue }}%</span>
                </div>
              </div>

              <!-- Progreso -->
              <div v-if="tag.presentationType === 'progress'" class="chart-panel">
                <div class="progress-track">
                  <span class="progress-fill" :style="{ width: `${progressValue}%`, background: '#4A90D9' }"></span>
                </div>
                <div class="tag-value">{{ progressValue }}%</div>
              </div>

              <!-- Ranking -->
              <div v-if="tag.presentationType === 'ranking'" class="chart-panel">
                <div v-for="(item, idx) in rankingData" :key="idx" class="rank-row">
                  <span class="rank-name">{{ item.label }}</span>
                  <strong class="rank-value">{{ item.value }}</strong>
                </div>
              </div>

              <!-- Valor numérico -->
              <div v-if="tag.presentationType === 'value'" class="chart-panel">
                <div class="tag-value">{{ randomValue }}</div>
                <div class="tag-legend">Valor de ejemplo</div>
              </div>
            </template>
          </div>

          <span class="resize-handle" @pointerdown.stop="startResize($event, tag.id)" />
        </article>
      </section>
    </b-container>
  </div>
</template>

<script>
import DashboardEtiquetas from "@/services/DashboardEtiquetas";

export default {
  name: "Dashboard",
  data() {
    return {
      maxTags: 50,
      showCreator: false,
      editingTagId: null,
      tags: [],
      activeTagId: null,
      zSeed: 10,
      interaction: null,
      saveTimeout: null,

      // Datos inventados para gráficos
      pieData: [
        { label: "Activos", value: 45, color: "#4A90D9", percent: 45 },
        { label: "Pasivos", value: 30, color: "#50C878", percent: 30 },
        { label: "Patrimonio", value: 25, color: "#FFB74D", percent: 25 }
      ],

      barData: [
        { label: "Ene", value: 100, height: 80, color: "#4A90D9" },
        { label: "Feb", value: 80, height: 65, color: "#50C878" },
        { label: "Mar", value: 60, height: 50, color: "#FFB74D" },
        { label: "Abr", value: 40, height: 35, color: "#EF5350" }
      ],

      rankingData: [
        { label: "Activos", value: 450 },
        { label: "Pasivos", value: 320 },
        { label: "Patrimonio", value: 280 },
        { label: "Ingresos", value: 200 }
      ],

      draft: {
        title: "",
        presentationType: "pie",
        color: "#f5faff",
        textColor: "#000000"
      },

      editDraft: {
        title: "",
        presentationType: "pie",
        color: "#f5faff",
        textColor: "#000000"
      },

      editOriginal: null
    };
  },

  computed: {
    progressValue() {
      return Math.floor(Math.random() * 40) + 60;
    },

    randomValue() {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0
      }).format(Math.floor(Math.random() * 1000000000) + 1000000);
    },

    linePointsData() {
      const points = [20, 40, 30, 60, 50, 80, 70, 90];
      return points.map((value, index) => ({
        x: (index / (points.length - 1)) * 100,
        y: 100 - (value / 100) * 80
      }));
    },

    linePoints() {
      return this.linePointsData.map(p => `${p.x},${p.y}`).join(" ");
    },

    areaPoints() {
      const data = this.linePointsData;
      return `${data[0].x},100 ${this.linePoints} ${data[data.length-1].x},100`;
    },

    pieGradient() {
      const total = this.pieData.reduce((s, d) => s + d.value, 0);
      let cursor = 0;
      const steps = this.pieData.map(d => {
        const percent = (d.value / total) * 100;
        const from = cursor;
        const to = cursor + percent;
        cursor = to;
        return `${d.color} ${from}% ${to}%`;
      });
      return `conic-gradient(${steps.join(", ")})`;
    },

    gaugeGradient() {
      const pct = this.progressValue;
      return `conic-gradient(from 180deg, #4A90D9 0% ${pct}%, rgba(255,255,255,0.2) ${pct}% 100%)`;
    }
  },

  mounted() {
    this.loadTags();
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  },

  beforeUnmount() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
  },

  methods: {
    async loadTags() {
      try {
        const res = await DashboardEtiquetas.getAll();
        const parsed = res?.data?.listDashboardEtiquetas || [];
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.tags = parsed.map((tag, index) => ({
            id: tag.id || Date.now() + index,
            title: tag.titulo || "Etiqueta",
            presentationType: tag.presentation_type || "pie",
            color: tag.color_fondo || "#f5faff",
            textColor: tag.color_texto || "#000000",
            left: tag.pos_x || 20 + (index * 20),
            top: tag.pos_y || 20 + (index * 20),
            width: tag.width_px || 240,
            height: tag.height_px || 180,
            zIndex: tag.z_index || index + 1
          }));
          this.zSeed = this.tags.reduce((max, t) => Math.max(max, t.zIndex), 10);
        } else {
          // No hay etiquetas en la base de datos, no crear etiquetas de ejemplo
          this.tags = [];
          this.zSeed = 10;
        }
      } catch (error) {
        // Si hay error o no hay datos, dejar la lista vacía
        this.tags = [];
        this.zSeed = 10;
      }
    },

    toggleCreator() {
      this.showCreator = !this.showCreator;
      if (this.showCreator) this.cancelTagEdit();
    },

    async createTag() {
      if (this.tags.length >= this.maxTags) {
        alert("Límite de etiquetas alcanzado");
        return;
      }

      if (!this.draft.title || this.draft.title.trim() === "") {
        alert("Por favor, ingresa un título para la etiqueta");
        return;
      }

      const freePosition = this.findAvailablePosition(240, 180);
      if (!freePosition) {
        alert("No hay espacio disponible");
        return;
      }

      const tag = {
        title: this.draft.title.trim(),
        presentationType: this.draft.presentationType,
        color: this.draft.color,
        textColor: this.draft.textColor,
        left: freePosition.left,
        top: freePosition.top,
        width: 240,
        height: 180,
        zIndex: ++this.zSeed
      };

      try {
        const res = await DashboardEtiquetas.create({
          titulo: tag.title,
          presentation_type: tag.presentationType,
          color_fondo: tag.color,
          color_texto: tag.textColor,
          pos_x: tag.left,
          pos_y: tag.top,
          width_px: tag.width,
          height_px: tag.height,
          z_index: tag.zIndex,
          idUser: this.$store.state.userId || 1
        });

        tag.id = res?.data?.id || Date.now();
        this.tags.push(tag);
        this.showCreator = false;
        this.draft = { title: "", presentationType: "pie", color: "#f5faff", textColor: "#000000" };
        
        // Mostrar mensaje de éxito
        if (this.$toast) {
          this.$toast.success("Etiqueta creada correctamente");
        }
      } catch (error) {
        console.error("Error creando etiqueta:", error);
        alert("Error al crear la etiqueta. Por favor, intenta de nuevo.");
      }
    },

    async deleteTag(tagId) {
      if (!confirm("¿Eliminar esta etiqueta?")) return;
      try {
        await DashboardEtiquetas.delete(tagId);
        this.tags = this.tags.filter(t => t.id !== tagId);
        if (this.$toast) {
          this.$toast.success("Etiqueta eliminada correctamente");
        }
      } catch (error) {
        console.error("Error eliminando etiqueta:", error);
        alert("Error al eliminar la etiqueta. Por favor, intenta de nuevo.");
      }
    },

    openTagEditor(tagId) {
      const tag = this.tags.find(t => t.id === tagId);
      if (!tag) return;
      this.editingTagId = tagId;
      this.editDraft = {
        title: tag.title,
        presentationType: tag.presentationType,
        color: tag.color,
        textColor: tag.textColor
      };
      this.editOriginal = { ...this.editDraft };
    },

    cancelTagEdit() {
      this.editingTagId = null;
      this.editOriginal = null;
    },

    async saveTagEdit() {
      const tag = this.tags.find(t => t.id === this.editingTagId);
      if (!tag) return;

      if (!this.editDraft.title || this.editDraft.title.trim() === "") {
        alert("El título no puede estar vacío");
        return;
      }

      tag.title = this.editDraft.title.trim();
      tag.presentationType = this.editDraft.presentationType;
      tag.color = this.editDraft.color;
      tag.textColor = this.editDraft.textColor;

      await this.persistTag(tag);
      this.cancelTagEdit();
      
      if (this.$toast) {
        this.$toast.success("Etiqueta actualizada correctamente");
      }
    },

    // Drag & Drop
    startDrag(event, tagId) {
      const tag = this.tags.find(t => t.id === tagId);
      if (!tag) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "drag",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        left: tag.left,
        top: tag.top
      };
    },

    startResize(event, tagId) {
      const tag = this.tags.find(t => t.id === tagId);
      if (!tag) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "resize",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        width: tag.width,
        height: tag.height
      };
    },

    onPointerMove(event) {
      if (!this.interaction) return;
      const tag = this.tags.find(t => t.id === this.interaction.tagId);
      if (!tag || !this.$refs.canvas) return;

      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      const deltaX = event.clientX - this.interaction.startX;
      const deltaY = event.clientY - this.interaction.startY;

      if (this.interaction.type === "drag") {
        const maxLeft = Math.max(canvasRect.width - tag.width, 0);
        const maxTop = Math.max(canvasRect.height - tag.height, 0);
        tag.left = Math.max(0, Math.min(this.interaction.left + deltaX, maxLeft));
        tag.top = Math.max(0, Math.min(this.interaction.top + deltaY, maxTop));
        this.scheduleAutoSave(tag);
      } else if (this.interaction.type === "resize") {
        tag.width = Math.max(200, Math.min(this.interaction.width + deltaX, 600));
        tag.height = Math.max(150, Math.min(this.interaction.height + deltaY, 400));
        this.scheduleAutoSave(tag);
      }
    },

    onPointerUp() {
      if (!this.interaction) return;
      const tag = this.tags.find(t => t.id === this.interaction.tagId);
      if (tag) {
        // Guardar inmediatamente al soltar
        this.persistTag(tag);
      }
      this.interaction = null;
    },

    scheduleAutoSave(tag) {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.persistTag(tag);
        this.saveTimeout = null;
      }, 300);
    },

    async persistTag(tag) {
      if (!tag || !tag.id) return;
      
      try {
        await DashboardEtiquetas.update({
          id: tag.id,
          titulo: tag.title,
          presentation_type: tag.presentationType,
          color_fondo: tag.color,
          color_texto: tag.textColor,
          pos_x: Math.round(tag.left),
          pos_y: Math.round(tag.top),
          width_px: Math.round(tag.width),
          height_px: Math.round(tag.height),
          z_index: tag.zIndex,
          idUser: this.$store.state.userId || 1
        });
      } catch (error) {
        console.error('Error guardando etiqueta:', error);
      }
    },

    raiseTag(tag) {
      tag.zIndex = ++this.zSeed;
    },

    findAvailablePosition(width, height) {
      const canvas = this.$refs.canvas;
      if (!canvas) return { left: 20, top: 20 };

      const maxLeft = Math.max(canvas.clientWidth - width, 0);
      const maxTop = Math.max(canvas.clientHeight - height, 0);

      for (let top = 10; top <= maxTop; top += 20) {
        for (let left = 10; left <= maxLeft; left += 20) {
          const candidate = { left, top, width, height };
          if (!this.hasCollision(candidate)) {
            return { left, top };
          }
        }
      }
      return null;
    },

    hasCollision(candidate) {
      return this.tags.some(tag => {
        return candidate.left < tag.left + tag.width &&
               candidate.left + candidate.width > tag.left &&
               candidate.top < tag.top + tag.height &&
               candidate.top + candidate.height > tag.top;
      });
    },

    tagStyle(tag) {
      return {
        left: `${tag.left}px`,
        top: `${tag.top}px`,
        width: `${tag.width}px`,
        height: `${tag.height}px`,
        backgroundColor: tag.color,
        color: tag.textColor,
        zIndex: tag.zIndex
      };
    }
  }
};
</script>

<style scoped>
.dashboard-lab-container {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.outside-toolbar {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
}

.outside-toolbar .btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  font-weight: 600;
  border-radius: 8px;
  background: #4A90D9;
  border: none;
  color: white;
  transition: all 0.2s;
}

.outside-toolbar .btn:hover {
  background: #3a7bc8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 144, 217, 0.3);
}

.outside-toolbar .btn .material-icons {
  font-size: 18px;
}

.tag-counter {
  font-size: 14px;
  color: #6c757d;
  background: #f1f3f5;
  padding: 5px 14px;
  border-radius: 20px;
  font-weight: 500;
}

.creator-panel {
  margin-bottom: 20px;
  animation: slideDown 0.3s ease;
}

.creator-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
}

.creator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 0.5fr;
  gap: 15px;
  margin: 12px 0;
}

.creator-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.tags-canvas {
  position: relative;
  min-height: 600px;
  background: white;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.canvas-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #adb5bd;
  font-size: 1.1rem;
}

.mini-tag {
  position: absolute;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
  border: 1px solid rgba(0,0,0,0.06);
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: grab;
}

.mini-tag:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.16);
}

.mini-tag.active {
  box-shadow: 0 0 0 2px #4A90D9, 0 4px 16px rgba(74, 144, 217, 0.25);
}

.mini-tag.editing {
  box-shadow: 0 0 0 2px #FFB74D, 0 4px 16px rgba(255, 183, 77, 0.25);
  z-index: 999 !important;
}

.mini-tag-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.mini-tag-header strong {
  font-size: 13px;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mini-tag-actions {
  display: flex;
  gap: 4px;
}

.mini-edit,
.mini-delete {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  font-size: 14px;
  color: #6c757d;
  border-radius: 4px;
  transition: all 0.2s;
}

.mini-edit:hover {
  background: rgba(74, 144, 217, 0.15);
  color: #4A90D9;
}

.mini-delete:hover {
  background: rgba(239, 83, 80, 0.15);
  color: #EF5350;
}

.mini-tag-body {
  flex: 1;
  overflow: auto;
  font-size: 12px;
}

.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  opacity: 0.3;
  transition: opacity 0.2s;
}

.resize-handle::after {
  content: "";
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-right: 2px solid #6c757d;
  border-bottom: 2px solid #6c757d;
}

.resize-handle:hover {
  opacity: 0.8;
}

/* Editor inline */
.inline-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inline-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.inline-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inline-field small {
  font-size: 10px;
  color: #6c757d;
}

.inline-color-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.inline-color-row input[type="color"] {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
}

/* Gráficos */
.chart-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  height: 100%;
  justify-content: center;
}

.mini-pie,
.mini-donut {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mini-donut {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.mini-donut::after {
  content: "";
  position: absolute;
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
}

.mini-donut span {
  position: relative;
  z-index: 1;
  font-weight: bold;
  font-size: 14px;
  color: #2c3e50;
}

.mini-series-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  justify-content: center;
}

.series-chip {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  color: #495057;
}

.series-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.mini-bars {
  display: flex;
  gap: 8px;
  height: 80px;
  align-items: flex-end;
  width: 100%;
  justify-content: center;
}

.mini-bar-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
  max-width: 40px;
}

.mini-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 100%;
}

.mini-bar-top-metrics {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mini-bar-value {
  font-size: 8px;
  color: #6c757d;
}

.mini-bar-fill {
  width: 100%;
  border-radius: 4px 4px 0 0;
  min-height: 8px;
  max-width: 30px;
}

.hbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-size: 10px;
}

.hbar-label {
  min-width: 50px;
  font-size: 9px;
  color: #495057;
}

.hbar-track {
  flex: 1;
  height: 16px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.hbar-fill {
  display: block;
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s;
}

.hbar-value {
  font-size: 9px;
  font-weight: bold;
  min-width: 40px;
  text-align: right;
}

.mini-line,
.mini-area {
  width: 100%;
  height: 80px;
  position: relative;
}

.line-svg {
  width: 100%;
  height: 100%;
}

.metric-meta {
  display: flex;
  gap: 12px;
  font-size: 9px;
  color: #6c757d;
}

.tag-value {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.tag-legend {
  font-size: 10px;
  color: #6c757d;
}

.progress-track {
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  overflow: hidden;
}

.progress-fill {
  display: block;
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s;
}

.mini-gauge {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.rank-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 2px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  font-size: 10px;
}

.rank-name {
  color: #495057;
}

.rank-value {
  color: #2c3e50;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .creator-grid {
    grid-template-columns: 1fr;
  }

  .mini-tag {
    position: relative !important;
    margin-bottom: 12px;
    width: 100% !important;
    height: auto !important;
    min-height: 180px;
    left: 0 !important;
    top: 0 !important;
  }

  .tags-canvas {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: auto;
  }

  .resize-handle {
    display: none;
  }

  .inline-grid {
    grid-template-columns: 1fr;
  }
}
</style>